import { Controller } from '@hotwired/stimulus';

/**
 * Invoice Line Uploader Controller (per-row child of `wps-form`)
 *
 * Lifecycle of a single invoice line's AI extraction:
 *   1. On `connect()`, read `data-local-id` from the row and retrieve the File
 *      from the parent `wps-form` controller via outlet.
 *   2. POST it as multipart to `/invoice_analyses` with an AbortController
 *      signal so a row-removal cancels the in-flight request.
 *   3. On response, update:
 *        - the hidden `analysis_status` input
 *        - the hidden `extracted_data` input (JSON-stringified)
 *        - the visible `amount_ht` input (only if empty AND extraction yielded one)
 *        - the extraction pill's state + label/icon (mirrors ExtractionPillComponent)
 *
 * Endpoint contract (see InvoiceAnalysesController):
 *   - { status: 'completed', extracted_data: { amount_ht?, … } } on success
 *   - { status: 'failed',    error: '…'  } on extractor error (200 OK)
 *   - { status: 'timeout' }                on timeout          (200 OK)
 *
 * State→pill mapping (simplified after ExtractionPillComponent Issue 2 cleanup):
 *   - 'pending' → bare spinner (clones <template id="pill-template-pending">)
 *   - all other statuses ('completed', 'failed', 'timeout', etc.) → pill host
 *     cleared (no template, no label). data-row-status still reflects the real
 *     underlying status so the parent wps-form's submit-block logic can query
 *     pending rows; the visual pill is fully decoupled from that attribute.
 *
 * Targets:
 *   - pill                : <span data-testid="extraction-pill"> (or its wrapper)
 *   - analysisStatusInput : hidden input for analysis_status
 *   - extractedDataInput  : hidden input for extracted_data (JSON string)
 *   - amountInput         : visible number input for amount_ht
 *
 * Outlets:
 *   - wps-form : the parent form controller, used to retrieve the File
 *
 * @example
 *   <tr data-controller="invoice-line-uploader"
 *       data-invoice-line-uploader-wps-form-outlet="[data-controller~='wps-form']"
 *       data-local-id="abc-123">
 *     <span data-invoice-line-uploader-target="pill">…</span>
 *     <input type="hidden"
 *            data-invoice-line-uploader-target="analysisStatusInput"
 *            name="...[analysis_status]">
 *     <input type="hidden"
 *            data-invoice-line-uploader-target="extractedDataInput"
 *            name="...[extracted_data]">
 *     <input type="number"
 *            data-invoice-line-uploader-target="amountInput"
 *            name="...[amount_ht]">
 *   </tr>
 */
// Pill states that have a server-rendered <template id="pill-template-X">.
// Only 'pending' has a template (bare spinner); all other statuses clear the host.
const PILL_TEMPLATE_STATES = new Set(['pending']);

export default class extends Controller {
  static targets = ['pill', 'analysisStatusInput', 'extractedDataInput', 'amountInput'];
  static outlets = ['wps-form', 'pdf-preview'];

  connect() {
    this.abortController = null;

    // The outlet may not be available synchronously in every clone path
    // (Stimulus connects the parent first in normal flow; this is defensive).
    if (!this.hasWpsFormOutlet) return;

    const localId = this.element.dataset.localId;
    if (!localId) return;

    // Skip portal-prefilled rows: they have an invoice_document_id and no File.
    const portalRow = !!this.element.querySelector('input[name$="[invoice_document_id]"]');
    if (portalRow) return;

    const file = this.wpsFormOutlet.getLineFile(localId);
    if (!file) return;

    this.#setPillState('pending');
    this.analyze(file);
  }

  disconnect() {
    this.#abort();
  }

  /**
   * POST the file to /invoice_analyses and update the row on response.
   *
   * @param {File} file
   */
  async analyze(file) {
    this.#abort(); // safety: cancel any prior in-flight request
    this.abortController = new AbortController();

    const formData = new FormData();
    formData.append('file', file);

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';

    try {
      const response = await fetch('/invoice_analyses', {
        method: 'POST',
        body: formData,
        signal: this.abortController.signal,
        credentials: 'same-origin',
        headers: {
          'X-CSRF-Token': csrfToken,
          Accept: 'application/json',
        },
      });

      // Even non-2xx responses are JSON in this endpoint's contract; tolerate
      // a non-JSON body (defensive) by treating it as a failure.
      let payload = {};
      try {
        payload = await response.json();
      } catch {
        payload = { status: 'failed' };
      }

      this.#applyAnalysisResult(payload);
    } catch (error) {
      if (error.name === 'AbortError') return; // expected on row removal
      this.#applyAnalysisResult({ status: 'failed', error: error.message });
    } finally {
      this.abortController = null;
    }
  }

  /**
   * Open the row's PDF preview in the shared `pdf-preview` modal.
   *
   * Resolves the URL in priority order:
   *   1. `data-file-url` on the row (server-rendered for portal-attached rows
   *      via wps-form#attachPortalCard which copies the source pool card's URL).
   *   2. The File object held in the parent `wps-form` controller's `lineMap`
   *      (manual upload path) → wraps it in a blob URL via URL.createObjectURL.
   *      The `pdf-preview` controller revokes the blob URL on modal close.
   *
   * Image uploads (JPG/PNG) bypass the PDF-shaped modal and open in a new tab,
   * since the modal's iframe + `#view=FitH` chrome is built for PDFs.
   *
   * Falls back to `window.open` when the pdf-preview outlet is not present
   * (defensive — should not happen on real pages).
   *
   * @param {Event} event
   */
  previewFile(event) {
    event?.preventDefault();

    const serverUrl = this.element.dataset.fileUrl;
    if (serverUrl) {
      this.#openInModalOrTab(serverUrl, this.#filenameForRow());
      return;
    }

    if (!this.hasWpsFormOutlet) return;
    const localId = this.element.dataset.localId;
    if (!localId) return;

    const file = this.wpsFormOutlet.getLineFile(localId);
    if (!file) return;

    if (file.type && file.type.startsWith('image/')) {
      window.open(URL.createObjectURL(file), '_blank', 'noopener');
      return;
    }

    const blobUrl = URL.createObjectURL(file);
    this.#openInModalOrTab(blobUrl, this.#filenameForRow(file));
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  #openInModalOrTab(url, filename) {
    if (this.hasPdfPreviewOutlet) {
      this.pdfPreviewOutlet.openWith(url, filename);
    } else {
      window.open(url, '_blank', 'noopener');
    }
  }

  #filenameForRow(file = null) {
    const customName = this.element.querySelector('input[name$="[custom_name]"]')?.value?.trim();
    return customName || file?.name || 'Document';
  }

  #abort() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Apply the JSON response from /invoice_analyses to the row's hidden inputs,
   * pill, and (when empty) the amount field.
   *
   * Race protection: HT and per-rate TVA inputs are skipped when their
   * `dataset.userDirty === 'true'` — that flag is set by the parent
   * `wps-form#onUserKeydown` on the architect's first keystroke. Without it,
   * a late extraction could overwrite an architect's typing once the row
   * exited `pending` (when the input was just re-enabled).
   *
   * @param {{ status: string, extracted_data?: object, error?: string }} payload
   */
  #applyAnalysisResult(payload) {
    const status = payload.status || 'failed';
    const extracted = payload.extracted_data || {};

    if (this.hasAnalysisStatusInputTarget) {
      this.analysisStatusInputTarget.value = status;
    }
    if (this.hasExtractedDataInputTarget) {
      this.extractedDataInputTarget.value = JSON.stringify(extracted);
    }

    // Only auto-fill the visible amount when the architect hasn't typed
    // anything yet AND the extractor returned a numeric value. The userDirty
    // guard catches the race where the architect started typing AFTER the
    // input was empty but BEFORE the extraction returned.
    if (
      this.hasAmountInputTarget &&
      this.amountInputTarget.dataset.userDirty !== 'true' &&
      this.amountInputTarget.value === '' &&
      extracted.amount_ht != null &&
      extracted.amount_ht !== ''
    ) {
      this.amountInputTarget.value = extracted.amount_ht;
      this.amountInputTarget.dispatchEvent(new Event('input', { bubbles: true }));
    }

    this.#setPillState(this.#pillStateForStatus(status));
  }

  /**
   * Map server status to a pill-template state.
   *
   * Only 'pending' maps to a template state (bare spinner). All other server
   * statuses ('completed', 'failed', 'timeout', etc.) return the raw status
   * string, which is NOT in PILL_TEMPLATE_STATES, so #setPillState will clear
   * the pill host. The return value is also written to data-row-status so the
   * parent wps-form controller can query the real underlying status.
   *
   * @param {string} status
   * @returns {string}
   */
  #pillStateForStatus(status) {
    return status;
  }

  /**
   * Update the row's extraction pill for the given state.
   *
   * For states with a server-rendered template (PILL_TEMPLATE_STATES), clone
   * the template into the pill host. For all other states (failed, manual,
   * unrecognized), clear the host — the empty amount field carries the
   * "fill manually" prompt, so a redundant warning pill is intentionally
   * suppressed.
   */
  #setPillState(state) {
    if (!this.hasPillTarget) return;

    const host = this.pillTarget;
    while (host.firstChild) host.removeChild(host.firstChild);

    if (PILL_TEMPLATE_STATES.has(state)) {
      const template = document.getElementById(`pill-template-${state}`);
      const node = template?.content?.firstElementChild;
      if (node) host.appendChild(node.cloneNode(true));
    }

    // Sync row-level data attribute so the parent form can query pending rows
    // in beforeSubmit. Decoupled from the visual pill: failed/manual rows
    // still surface their state here even though no pill is rendered.
    const row = this.element;
    if (row.dataset.rowStatus !== state) {
      row.dataset.rowStatus = state;
    }

    // Notify the parent form controller so it can recompute submit-block state.
    this.dispatch('row-state-changed', {
      prefix: 'wps-form',
      detail: { localId: row.dataset.localId, state },
      bubbles: true,
    });
  }
}
